'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import Cookies from 'js-cookie';
import './Notification.css';
import { IconButton, Badge } from '@mui/material';
import NotificationsIcon from '../../assets/Notification/notification.svg';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { axiosInstance } from '../../utils/tokenUtils';
import { Clock, Wallet1, TickCircle, NotificationBing } from 'iconsax-react';

import ReviewIcon from '../../assets/Notification/star.svg';
import CancelledIcon from '../../assets/Notification/session-cancelled.svg';
import ReminderIcon from '../../assets/Notification/reminder.svg';
import { differenceInCalendarDays } from 'date-fns';
import ExtendSessionRespondDialog from './ExtendSessionRespondDialog';

const categorizeNotifications = notificationList => {
  const categorized = {
    today: [],
    yesterday: [],
    earlier: [],
  };

  const now = new Date();

  notificationList.forEach(notification => {
    const notificationDate = new Date(notification.created_at);
    const diffDays = differenceInCalendarDays(now, notificationDate);

    let timeString;
    if (diffDays === 0) {
      const diffHours = Math.floor((now - notificationDate) / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor((now - notificationDate) / (1000 * 60));
        timeString = `${diffMinutes} min ago`;
      } else {
        timeString = `${diffHours} hours ago`;
      }
    } else if (diffDays === 1) {
      timeString = `Yesterday at ${notificationDate.getHours()}:${notificationDate.getMinutes().toString().padStart(2, '0')} ${notificationDate.getHours() >= 12 ? 'PM' : 'AM'}`;
    } else {
      timeString = `${diffDays} days ago`;
    }

    const formattedNotification = {
      id: notification.id,
      type: notification.type,
      notification_type: notification.notification_type,
      title: notification.title,
      message: notification.message,
      description: notification.description,
      meta_data: notification.meta_data ?? {},
      time: timeString,
      read: Boolean(notification.is_read),
      created_at: notification.created_at,
    };

    if (diffDays === 0) {
      categorized.today.push(formattedNotification);
    } else if (diffDays === 1) {
      categorized.yesterday.push(formattedNotification);
    } else {
      categorized.earlier.push(formattedNotification);
    }
  });

  Object.keys(categorized).forEach(key => {
    categorized[key].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  });

  return categorized;
};

const buildNotificationsWsUrl = token => {
  const explicit = process.env.REACT_APP_WS_BASE_URL;
  const restBase = process.env.REACT_APP_SERVER_API_URL;

  const toWsBase = base => {
    if (!base) return null;
    const withProto = /^(https?:|wss?:)/.test(base) ? base : `https://${base}`;
    try {
      const url = new URL(withProto);
      const scheme = url.protocol === 'https:' || url.protocol === 'wss:' ? 'wss' : 'ws';
      return `${scheme}://${url.host}`;
    } catch {
      return null;
    }
  };

  let wsBase = toWsBase(explicit);
  if (!wsBase) wsBase = toWsBase(restBase);
  if (!wsBase) wsBase = toWsBase(window.location.origin);
  if (!wsBase) return null;
  return `${wsBase}/notifications/ws?token=${encodeURIComponent(token)}`;
};

export default function Notification() {
  const [open, setOpen] = useState(false);
  const socketRef = useRef(null);
  const notificationRef = useRef(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [error, setError] = useState(null);
  const [lastReconnectTime, setLastReconnectTime] = useState(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_INTERVAL = 10 * 60 * 1000; // 10 minutes in milliseconds

  // Initialize notifications state
  const [notifications, setNotifications] = useState({
    today: [],
    yesterday: [],
    earlier: [],
  });

  const [serverUnreadCount, setServerUnreadCount] = useState(0);
  const [extensionDialog, setExtensionDialog] = useState({
    open: false,
    meta: null,
    summaryMessage: '',
  });

  const fetchPreviousNotifications = useCallback(async () => {
    setError(null);

    try {
      const response = await axiosInstance.get('/notifications');
      console.log('Previous notifications fetched:', response.data);

      if (response.data && Array.isArray(response.data)) {
        const processedNotifications = categorizeNotifications(response.data);
        setNotifications(processedNotifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to load notifications. Please try again later.');
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/notifications/unread-count');
      console.log('Unread notifications count fetched:', response.data);

      if (response.data && response.data['unread-notifications'] !== undefined) {
        setServerUnreadCount(response.data['unread-notifications']);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  useEffect(() => {
    fetchPreviousNotifications();
    fetchUnreadCount();
  }, [fetchPreviousNotifications, fetchUnreadCount]);

  const connectWebSocket = async (forceTokenRefresh = false) => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    const now = Date.now();
    // Check if we've tried too recently (within 10 minutes)
    if (reconnectAttempts > 0 && now - lastReconnectTime < RECONNECT_INTERVAL) {
      console.log(`Skipping reconnection attempt - waiting for 10-minute interval to pass`);
      return;
    }

    // If we've reached the maximum number of attempts, don't try again
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.log(`Maximum reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Giving up.`);
      return;
    }

    // Update the last reconnection time
    setLastReconnectTime(now);

    // Get token from Cookies
    const token = Cookies.get('access_token');
    if (!token) {
      console.error('No token found in cookies');
      return;
    }

    const wsUrl = buildNotificationsWsUrl(token);
    if (!wsUrl) {
      console.error(
        'Could not build notification WebSocket URL; set REACT_APP_SERVER_API_URL or REACT_APP_WS_BASE_URL'
      );
      return;
    }
    console.log('Connecting to Notification WebSocket:', wsUrl.replace(/token=[^&]+/, 'token=***'));

    const newSocket = new WebSocket(wsUrl);

    newSocket.onopen = () => {
      console.log('Notification WebSocket connected successfully');
      socketRef.current = newSocket;
      setReconnectAttempts(0);
    };

    newSocket.onmessage = event => {
      console.log('Notification WebSocket message received:', event.data);
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'new_notification') {
          // Handle new notification
          addNewNotification(data.notification);
        } else if (data.type === 'notification_read') {
          // Handle notification read
          markNotificationAsRead(data.notification_id);
        } else if (data.type === 'notification_deleted') {
          // Simply ignore this notification type now
        } else if (data.msg && data.title) {
          // Handle direct message format
          const notification = {
            id: Date.now(), // Generate a unique ID
            type: data.title.toLowerCase().includes('cancelled') ? 'cancelled' : 'general',
            title: data.title,
            message: data.msg,
            created_at: new Date().toISOString(),
            read: false,
          };
          addNewNotification(notification);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    newSocket.onerror = error => {
      console.error('WebSocket error:', error);
    };

    newSocket.onclose = event => {
      console.log('WebSocket closed:', event);
      socketRef.current = null;

      // Attempt to reconnect after a delay, but only if we haven't exceeded max attempts
      if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        const nextAttempt = reconnectAttempts + 1;
        setReconnectAttempts(nextAttempt);

        console.log(`WebSocket reconnection attempt ${nextAttempt}/${MAX_RECONNECT_ATTEMPTS}`);
      } else {
        console.log(`Maximum WebSocket reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached`);
      }
    };
  };

  useEffect(() => {
    connectWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset reconnection attempts periodically
  useEffect(() => {
    // Reset reconnection attempts after 1 hour
    const resetInterval = setInterval(
      () => {
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.log('Resetting WebSocket reconnection attempts counter');
          setReconnectAttempts(0);
        }
      },
      60 * 60 * 1000
    ); // 1 hour

    return () => clearInterval(resetInterval);
  }, [reconnectAttempts]);

  // Handle connection errors by refreshing token

  const addNewNotification = notification => {
    // When a new notification comes in via WebSocket,
    // add it to our notifications state

    // Ensure notification has the necessary fields
    const processedNotification = {
      ...notification,
      type: notification.type,
      notification_type: notification.notification_type,
      message: notification.message || notification.msg,
      description: notification.description,
      title: notification.title,
      meta_data: notification.meta_data ?? {},
      read: notification.read || false,
      is_read: notification.is_read || false,
      created_at: notification.created_at || new Date().toISOString(),
    };

    const processedNotifications = categorizeNotifications([processedNotification]);

    setNotifications(prev => {
      const newNotifications = { ...prev };

      // Merge the new notification into the existing categories
      Object.keys(processedNotifications).forEach(timeframe => {
        if (processedNotifications[timeframe].length > 0) {
          newNotifications[timeframe] = [...processedNotifications[timeframe], ...prev[timeframe]];
        }
      });

      return newNotifications;
    });
  };

  const markNotificationAsRead = async notificationId => {
    const updatedNotifications = { ...notifications };

    Object.keys(updatedNotifications).forEach(timeframe => {
      updatedNotifications[timeframe] = updatedNotifications[timeframe].map(notification =>
        notification.id === notificationId ? { ...notification, read: true } : notification
      );
    });

    setNotifications(updatedNotifications);

    // Update server unread count
    fetchUnreadCount();

    // Send read status to server via API as well
    try {
      await axiosInstance.post(`/notifications/${notificationId}/read`);
      console.log(`Notification ${notificationId} marked as read on server via API`);
    } catch (error) {
      console.error(`Failed to mark notification ${notificationId} as read on server:`, error);
    }
  };

  const handleOpen = event => {
    // Fetch latest notifications and unread count when opening the panel
    fetchPreviousNotifications();
    fetchUnreadCount();

    setOpen(true);

    // Mark notifications as read when opening the panel
    if (!open) {
      // Get all unread notification IDs
      const unreadIds = [];
      Object.values(notifications).forEach(section => {
        section.forEach(notification => {
          if (!notification.read) {
            unreadIds.push(notification.id);
          }
        });
      });

      // Send read status to server via both WebSocket and API
      if (unreadIds.length > 0) {
        if (socketRef.current) {
          socketRef.current.send(
            JSON.stringify({
              type: 'mark_read',
              notification_ids: unreadIds,
            })
          );
        }

        // Also mark as read via API
        unreadIds.forEach(async id => {
          try {
            await axiosInstance.post(`/notifications/${id}/read`);
          } catch (error) {
            console.error(`Failed to mark notification ${id} as read:`, error);
          }
        });
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const isSessionExtensionRequestNotification = n => {
    const raw = n.notification_type ?? n.type ?? '';
    return String(raw).toLowerCase() === 'session_extension_request';
  };

  const handleNotificationRowClick = notification => {
    if (!notification.read) {
      markNotificationAsRead(notification.id);
    }
    const meta = notification.meta_data || {};
    const extId = meta.extension_request_id;
    if (isSessionExtensionRequestNotification(notification) && extId) {
      const summaryMessage = notification.message || notification.description || '';
      setExtensionDialog({
        open: true,
        meta,
        summaryMessage,
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    const updatedNotifications = { ...notifications };
    const unreadIds = [];

    Object.keys(updatedNotifications).forEach(timeframe => {
      updatedNotifications[timeframe] = updatedNotifications[timeframe].map(notification => {
        if (!notification.read) {
          unreadIds.push(notification.id);
        }
        return {
          ...notification,
          read: true,
        };
      });
    });

    setNotifications(updatedNotifications);
    setServerUnreadCount(0); // Update server unread count in UI immediately

    // Send read status to server via both methods
    if (unreadIds.length > 0) {
      if (socketRef.current) {
        socketRef.current.send(
          JSON.stringify({
            type: 'mark_read',
            notification_ids: unreadIds,
          })
        );
      }

      // Also mark as read via API - updated endpoint
      try {
        const response = await axiosInstance.post(`/notifications/mark-all-as-read`);
        console.log('All notifications marked as read on server via API:', response.data);
      } catch (error) {
        console.error('Failed to mark all notifications as read:', error);
      }
    }
  };

  const getNotificationIcon = type => {
    switch (type) {
      case 'booking_created':
        return (
          <div className="notification-icon-circle session-booked">
            <Clock size="18" variant="Outline" />
          </div>
        );
      case 'session_booked':
        return (
          <div className="notification-icon-circle session-booked">
            <Clock size="18" variant="Outline" />
          </div>
        );
      case 'session_completed':
        return (
          <div className="notification-icon-circle review">
            <TickCircle size="18" variant="Outline" />
          </div>
        );
      case 'review':
        return (
          <div className="notification-icon-circle review">
            <img src={ReviewIcon} className="notification-icon" alt="Review" />
          </div>
        );
      case 'schedule_change':
        return (
          <div className="notification-icon-circle schedule-change">
            <Clock size="18" variant="Outline" />
          </div>
        );
      case 'payment':
      case 'session_payment_processed':
      case 'session_payment_proceed':
        return (
          <div className="notification-icon-circle payment">
            <Wallet1 size="18" variant="Outline" />
          </div>
        );
      case 'session_reminder':
        return (
          <div className="notification-icon-circle reminder">
            <Clock size="18" variant="Outline" />
          </div>
        );
      case 'session_extension_request':
        return (
          <div className="notification-icon-circle schedule-change">
            <ScheduleIcon sx={{ fontSize: 18, color: '#0A5EF3' }} />
          </div>
        );
      case 'booking_cancelled':
        return (
          <div className="notification-icon-circle cancelled">
            <img src={CancelledIcon} className="notification-icon" alt="Cancelled" />
          </div>
        );
      case 'cancelled':
        return (
          <div className="notification-icon-circle cancelled">
            <img src={CancelledIcon} className="notification-icon" alt="Cancelled" />
          </div>
        );
      case 'reminder':
        return (
          <div className="notification-icon-circle reminder">
            <img src={ReminderIcon} className="notification-icon" alt="Reminder" />
          </div>
        );
      default:
        return (
          <div className="notification-icon-circle general">
            <NotificationBing size="18" variant="Outline" />
          </div>
        );
    }
  };

  // Count unread notifications - use server count if available, otherwise use local count
  const unreadCount =
    serverUnreadCount !== undefined && serverUnreadCount !== null
      ? serverUnreadCount
      : Object.values(notifications)
          .flat()
          .filter(notification => !notification.read).length;

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Format notification text to be more presentable
  const formatNotificationText = text => {
    if (!text) return '';

    // First, handle special case replacements
    const specialReplacements = {
      'SessionType.ONE_ON_ONE': 'One-on-One',
      'one_on_one Session': 'One-on-One Session',
      one_on_one: 'One-on-One',
    };

    let formattedText = text;

    // Apply special replacements first
    Object.entries(specialReplacements).forEach(([key, value]) => {
      formattedText = formattedText.replace(new RegExp(key, 'g'), value);
    });

    // Replace remaining underscores with spaces and capitalize each word
    formattedText = formattedText
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    // Handle cases where we already have spaces
    formattedText = formattedText
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return formattedText;
  };

  return (
    <div className={`notification-container ${open ? 'notification-container-active' : ''}`}>
      <IconButton onClick={handleOpen} aria-label="notifications">
        <Badge badgeContent={unreadCount} color="error">
          <img src={NotificationsIcon} alt="" />
        </Badge>
      </IconButton>

      {open && (
        <>
          <div className="notification-backdrop" onClick={handleClose}></div>
          <div className="notification-popup" ref={notificationRef}>
            <div className="notification-header">
              <h3>Notifications</h3>
              <button className="mark-all-button" onClick={handleMarkAllAsRead}>
                Mark all as read
              </button>
            </div>

            <div className="notification-content-container">
              {error && (
                <div className="notification-error">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                  <button onClick={fetchPreviousNotifications}>Retry</button>
                </div>
              )}

              {/* Today's notifications */}
              {notifications.today.length > 0 && (
                <>
                  <div className="notification-section-title">Today</div>
                  {notifications.today.map(notification => (
                    <div
                      key={notification.id}
                      className={`notification-item ${notification.read ? 'read' : ''}`}
                      onClick={() => handleNotificationRowClick(notification)}
                    >
                      {!notification.read && <div className="unread-indicator"></div>}
                      <div className="notification-content">
                        {getNotificationIcon(notification.type || notification.notification_type)}
                        <div className="notification-details">
                          <div className="notification-title">
                            {formatNotificationText(notification.title)}
                          </div>
                          <div className="notification-message">
                            {formatNotificationText(
                              notification.message || notification.description
                            )}
                          </div>
                          <div className="notification-time">
                            <AccessTimeIcon fontSize="small" /> {notification.time}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Yesterday's notifications */}
              {notifications.yesterday.length > 0 && (
                <>
                  <div className="notification-section-title">Yesterday</div>
                  {notifications.yesterday.map(notification => (
                    <div
                      key={notification.id}
                      className={`notification-item ${notification.read ? 'read' : ''}`}
                      onClick={() => handleNotificationRowClick(notification)}
                    >
                      {!notification.read && <div className="unread-indicator"></div>}
                      <div className="notification-content">
                        {getNotificationIcon(notification.type || notification.notification_type)}
                        <div className="notification-details">
                          <div className="notification-title">
                            {formatNotificationText(notification.title)}
                          </div>
                          <div className="notification-message">
                            {formatNotificationText(
                              notification.message || notification.description
                            )}
                          </div>
                          <div className="notification-time">
                            <AccessTimeIcon fontSize="small" /> {notification.time}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Earlier notifications */}
              {notifications.earlier.length > 0 && (
                <>
                  <div className="notification-section-title">Earlier</div>
                  {notifications.earlier.map(notification => (
                    <div
                      key={notification.id}
                      className={`notification-item ${notification.read ? 'read' : ''}`}
                      onClick={() => handleNotificationRowClick(notification)}
                    >
                      {!notification.read && <div className="unread-indicator"></div>}
                      <div className="notification-content">
                        {getNotificationIcon(notification.type || notification.notification_type)}
                        <div className="notification-details">
                          <div className="notification-title">
                            {formatNotificationText(notification.title)}
                          </div>
                          <div className="notification-message">
                            {formatNotificationText(
                              notification.message || notification.description
                            )}
                          </div>
                          <div className="notification-time">
                            <AccessTimeIcon fontSize="small" /> {notification.time}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Empty state if there are no notifications */}
              {Object.values(notifications).flat().length === 0 && (
                <div className="empty-notifications">
                  <p>No notifications at this time</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <ExtendSessionRespondDialog
        open={extensionDialog.open}
        onClose={() =>
          setExtensionDialog({
            open: false,
            meta: null,
            summaryMessage: '',
          })
        }
        meta={extensionDialog.meta}
        summaryMessage={extensionDialog.summaryMessage}
        onAfterAction={() => {
          fetchPreviousNotifications();
          fetchUnreadCount();
        }}
      />
    </div>
  );
}
